import React, { useContext, useEffect } from 'react';
import useAnalytics from 'helpers/useAnalytics';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import colors from 'styles/colors';
import makeStyles from '@material-ui/core/styles/makeStyles';
import classNames from 'classnames';
import SourceContext from 'helpers/SourceContext';
import handleAuthorizeSource from 'helpers/handleAuthorizeSource';
import makeSourceCreateBaseUrl from 'pages/Directory/helpers/makeSourceCreateBaseUrl';

import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import Typography from '@material-ui/core/Typography';
import CardHeader from '@material-ui/core/CardHeader';
import Base from 'pages/Directory/Base';

import {
  AUTHORIZE_DATA_ACCESS_URL,
  AZURE_SOURCE_TYPE_KEY,
  CHOOSE_SYNC_GROUPS_URL,
  CSV_SOURCE_TYPE_KEY,
  GOOGLE_SOURCE_TYPE_KEY,
  MAP_ATTRIBUTES_URL,
} from 'pages/Directory/consts';

import sourceData from 'pages/Directory/sourceData';
import * as Types from 'pages/Directory/typings';

function Authorization() {
  const analytics = useAnalytics();
  const location = useLocation();
  const history = useHistory();
  const query = queryString.parse(location.search);
  const { authStatus, type } = query;
  const classes = useStyles();
  const sourceContext = useContext(SourceContext) as Types.SourceContext;
  const { sourceInfo } = sourceContext;
  const sourceType = sourceInfo.type || type;
  const baseUrl = makeSourceCreateBaseUrl(sourceInfo.directoryId, sourceInfo.sourceId);
  const isAzureSource = sourceType === AZURE_SOURCE_TYPE_KEY;
  const isGoogleSource = sourceType === GOOGLE_SOURCE_TYPE_KEY;
  const { sourceId } = sourceInfo;

  useEffect(() => {
    if (authStatus === 'error') {
      analytics.track('Wizard - Authorize Step Errored', {
        category: 'AD - Directories',
        label: sourceType,
      });
    }
  }, [authStatus]);

  const authContent =
    authStatus === 'success'
      ? `You have successfully authorized with Sift. Click “Next” to continue`
      : authStatus === 'error'
      ? `There was an Error Authorizing Sift with ${
          isAzureSource ? 'Azure AD' : 'Google Workspace'
        }, please try again.`
      : 'Unauthorized';

  const handleAuthorize = () => {
    analytics.track('Wizard - Authorize Button Clicked', {
      category: 'AD - Directories',
      label: sourceType,
    });

    const query = {
      successRedirectUri: `${window.location.origin}${baseUrl}${AUTHORIZE_DATA_ACCESS_URL}?authStatus=success&type=${sourceType}`,
      errorRedirectUri: `${window.location.origin}${baseUrl}${AUTHORIZE_DATA_ACCESS_URL}?authStatus=error&type=${sourceType}`,
    };

    handleAuthorizeSource(query, sourceId);
  };

  const handleNext = () => {
    if (isAzureSource || isGoogleSource) {
      history.push(`${baseUrl}${CHOOSE_SYNC_GROUPS_URL}`);
    } else {
      history.push(`${baseUrl}${MAP_ATTRIBUTES_URL}`);
    }

    analytics.track('Wizard - Authorize Step Completed', {
      category: 'AD - Directories',
      label: sourceType,
    });
  };

  return (
    <Base
      onNext={authStatus === 'success' ? handleNext : handleAuthorize}
      buttonText={authStatus === 'success' ? 'Next' : 'Authorize'}
      currentStep={1}
    >
      {sourceType !== CSV_SOURCE_TYPE_KEY && (
        <CardHeader
          title={sourceData[sourceType].header}
          subheader={sourceData[sourceType].subtitle}
        />
      )}
      <div className={classes.authStatus}>
        <span className={classes.authTitle}>Authorization Status</span>
        <div className={classes.authContent}>
          <FiberManualRecordIcon
            fontSize="small"
            className={classNames(
              classes.indicator,
              // @ts-ignore
              authStatus ? classes[authStatus] : classes.notAuthed,
            )}
          />
          <Typography variant="body2">{authContent}</Typography>
        </div>
      </div>
    </Base>
  );
}

const useStyles = makeStyles(theme => {
  return {
    authStatus: {
      marginLeft: theme.spacing(3),
      display: 'flex',
      alignItems: 'center',
    },
    authTitle: {
      color: theme.palette.primary.main,
    },
    authContent: {
      display: 'flex',
      margin: theme.spacing(3),
    },
    indicator: {
      marginRight: theme.spacing(),
    },
    notAuthed: {
      fill: colors.brandColors.indicatorColors.tiger,
    },
    success: {
      fill: colors.brandColors.tertiaryPalette.lime,
    },
    error: {
      fill: colors.error,
    },
  };
});

export default Authorization;
